import React, { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getUserProfile, updateUserProfile } from '../services/userDataService'
import PreviewWall from './PreviewWall'
import educationData from './educationData'

// One accent per module — matches desktop (Education.jsx). Flows through the
// module icon tile, active lesson pill, in-content section headers and links.
const MODULE_ACCENTS = {
  1: '#60a5fa',
  2: '#34d399',
  3: '#f59e0b',
  4: '#a78bfa',
  5: '#22d3ee',
  6: '#ec4899',
  7: '#fbbf24'
}
const moduleAccent = (id) => MODULE_ACCENTS[id] || '#60a5fa'

// Renders shared lesson content for mobile. Same markdown transform as desktop,
// plus a mobile-only tweak: a floated image at the very top (used on desktop so
// text wraps beside it) is un-floated and moved below the first paragraph so it
// reads cleanly in a single narrow column.
function renderLessonContent(content) {
  let src = content
  const leadFloat = /^<div style="float: right;[^]*?<\/div>\s*\n\n/
  const m = src.match(leadFloat)
  if (m) {
    const imgBlock = m[0].trim().replace('float: right;', 'float: none;')
    src = src.slice(m[0].length)
    // Insert the un-floated image after the first paragraph (first \n\n break).
    const brk = src.indexOf('\n\n')
    if (brk === -1) {
      src = src + '\n\n' + imgBlock
    } else {
      src = src.slice(0, brk) + '\n\n' + imgBlock + src.slice(brk)
    }
  }
  return src
    .replace(/# (.*)/g, '<h2 class="edu-header">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="edu-bold">$1</strong>')
    .replace(/\n/g, '<br />')
}

// Flat, ordered [moduleId, lessonId] list — drives the overall progress totals.
const ALL_LESSONS = Object.entries(educationData)
  .sort(([a], [b]) => parseInt(a) - parseInt(b))
  .flatMap(([mId, m]) =>
    Object.keys(m.lessons)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(lId => [parseInt(mId), parseInt(lId)])
  )
const TOTAL_LESSONS = ALL_LESSONS.length

// Records the moment the whole course was finished. Lives in the same JSONB blob
// as the lesson ticks (shared with desktop), underscore-prefixed so it can never
// collide with a "moduleId-lessonId" key or be picked up by the doneCount filter.
const COURSE_KEY = '_completedAt'
const MODULE_IDS = Object.keys(educationData).map(Number).sort((a, b) => a - b)
const MODULE_COUNT = MODULE_IDS.length

// Seal ring geometry: one arc per module, sharing the circle's circumference.
const SEAL_R = 52
const SEAL_C = 2 * Math.PI * SEAL_R
const SEAL_PITCH = SEAL_C / MODULE_COUNT
const SEAL_DASH = SEAL_PITCH - 7 // 7px gap between arcs
const TICK_LEN = 60

// Counts from zero to `end` after `delay` ms. Used for the completion stats.
function CountUp({ end, delay = 0, duration = 900 }) {
  const [n, setN] = useState(0)
  React.useEffect(() => {
    let raf = null
    let start = null
    const step = (t) => {
      if (start === null) start = t
      const p = Math.min((t - start) / duration, 1)
      setN(Math.round(end * (1 - Math.pow(1 - p, 3))))
      if (p < 1) raf = requestAnimationFrame(step)
    }
    const timer = setTimeout(() => { raf = requestAnimationFrame(step) }, delay)
    return () => { clearTimeout(timer); if (raf) cancelAnimationFrame(raf) }
  }, [end, delay, duration])
  return <>{n}</>
}

// Ring of module-accent arcs drawing themselves in order, then the tick strokes on.
function CompletionSeal() {
  return (
    <div style={{ position: 'relative', width: '112px', height: '112px', margin: '0 auto 20px' }}>
      <div className="edu-seal-glow" />
      <svg width="112" height="112" viewBox="0 0 120 120" style={{ position: 'relative', display: 'block', transform: 'rotate(-90deg)' }}>
        <circle cx="60" cy="60" r={SEAL_R} fill="none" stroke="#16203a" strokeWidth="4" />
        {MODULE_IDS.map((mId, i) => (
          <circle
            key={mId}
            cx="60" cy="60" r={SEAL_R}
            fill="none"
            stroke={moduleAccent(mId)}
            strokeWidth="4"
            strokeLinecap="round"
            className="edu-seal-arc"
            style={{
              strokeDasharray: `${SEAL_DASH} ${SEAL_C - SEAL_DASH}`,
              strokeDashoffset: -i * SEAL_PITCH,
              animationDelay: `${i * 0.1}s`
            }}
          />
        ))}
        <path
          d="M40 61 L53 74 L80 45"
          fill="none"
          stroke="#34d399"
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="edu-seal-tick"
          style={{ transform: 'rotate(90deg)', transformOrigin: '60px 60px' }}
        />
      </svg>
    </div>
  )
}

const styles = {
  container: {
    backgroundColor: '#010714',
    minHeight: '100vh',
    padding: '16px',
    boxSizing: 'border-box'
  },
  header: {
    backgroundColor: '#0d1526',
    borderRadius: '12px',
    border: '1px solid #1b2438',
    padding: '18px',
    marginBottom: '14px'
  },
  title: {
    fontSize: '22px',
    fontWeight: '800',
    letterSpacing: '-0.01em',
    color: '#f1f5f9',
    margin: 0
  },
  subtitle: {
    fontSize: '13px',
    color: '#8b97ab',
    margin: '4px 0 0 0'
  },
  moduleCard: {
    backgroundColor: '#0d1526',
    borderRadius: '12px',
    border: '1px solid #1b2438',
    marginBottom: '10px'
  },
  moduleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 16px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#f1f5f9',
    cursor: 'pointer'
  },
  contentCard: {
    backgroundColor: '#0b1220',
    borderRadius: '10px',
    border: '1px solid #1b2438',
    padding: '18px'
  },
  supportLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 12px',
    borderRadius: '8px',
    border: '1px solid #26354f',
    background: 'transparent',
    color: '#8b97ab',
    fontSize: '11.5px',
    fontWeight: '600',
    textDecoration: 'none'
  }
}

export default function Education() {
  const { user, hasAccess, hadTrial, loading: authLoading } = useAuth()
  const isLocked = !authLoading && !hasAccess('education')
  const [expandedModules, setExpandedModules] = useState({})
  const [expandedLessons, setExpandedLessons] = useState({})

  // Per-lesson completion, stored in Supabase (user_profiles.education_progress).
  // Same column as desktop, so progress carries across devices.
  const [eduProgress, setEduProgress] = useState({})
  React.useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    getUserProfile(user.id).then(({ data }) => {
      if (!cancelled) setEduProgress(data?.education_progress || {})
    })
    return () => { cancelled = true }
  }, [user?.id])

  // Celebrations. Both only ever fire from a user tick below, never from the
  // initial profile load, so nothing pops on arrival at an already-finished course.
  const [celebrating, setCelebrating] = useState(false)
  const [moduleToast, setModuleToast] = useState(null)
  const [justTicked, setJustTicked] = useState(null)

  React.useEffect(() => {
    if (!moduleToast || moduleToast.leaving) return
    const t = setTimeout(() => setModuleToast(prev => (prev ? { ...prev, leaving: true } : null)), 3400)
    return () => clearTimeout(t)
  }, [moduleToast])
  React.useEffect(() => {
    if (!moduleToast || !moduleToast.leaving) return
    const t = setTimeout(() => setModuleToast(null), 320)
    return () => clearTimeout(t)
  }, [moduleToast])

  const isDone = (m, l) => !!eduProgress[`${m}-${l}`]
  const setDone = (m, l, val) => {
    const key = `${m}-${l}`
    if (!!eduProgress[key] === !!val) return
    const prev = eduProgress
    const next = { ...prev }
    if (val) next[key] = true
    else delete next[key]

    if (val) {
      setJustTicked(key)
      const total = ALL_LESSONS.filter(([mm, ll]) => !!next[`${mm}-${ll}`]).length
      const siblings = Object.keys(educationData[m].lessons).map(Number)
      if (total === TOTAL_LESSONS && !prev[COURSE_KEY]) {
        // Stamped in the same write as the tick, so it can't half-save.
        next[COURSE_KEY] = new Date().toISOString()
        setCelebrating(true)
      } else if (siblings.every(ll => !!next[`${m}-${ll}`])) {
        setModuleToast({ mId: m })
      }
    }

    setEduProgress(next) // optimistic
    if (user?.id) {
      updateUserProfile(user.id, { education_progress: next }).then(({ error }) => {
        if (error) setEduProgress(prev) // rollback on failure, celebration included
      })
    }
  }
  const doneCount = ALL_LESSONS.filter(([m, l]) => isDone(m, l)).length
  const pct = TOTAL_LESSONS > 0 ? Math.round((doneCount / TOTAL_LESSONS) * 100) : 0

  const handleModuleClick = (moduleId) => {
    if (expandedModules[moduleId]) {
      setExpandedModules(prev => ({ ...prev, [moduleId]: false }))
    } else {
      setExpandedModules({ [moduleId]: true })
      setTimeout(() => {
        const contentElement = document.getElementById(`module-lessons-${moduleId}`)
        if (contentElement) {
          contentElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
        }
      }, 150)
    }
  }

  const handleLessonClick = (moduleId, lessonId) => {
    const lessonKey = `${moduleId}-${lessonId}`
    setExpandedLessons(prev => ({ ...prev, [lessonKey]: !prev[lessonKey] }))
    if (!expandedLessons[lessonKey]) {
      setTimeout(() => {
        const lessonElement = document.getElementById(`lesson-${moduleId}-${lessonId}`)
        if (lessonElement) {
          lessonElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
        }
      }, 150)
    }
  }

  if (isLocked) {
    return (
      <div style={{ ...styles.container, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <PreviewWall
          title="Learn to beat the bookmakers"
          features={['Step-by-step betting strategy modules', 'EV, arbitrage, middles & bonus explained', 'Bankroll management & staking guides']}
          planName="Premium"
          accent="#60a5fa"
          hadTrial={hadTrial}
        />
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <style>{`
        .edu-header {
          font-size: 17px;
          font-weight: 800;
          letter-spacing: -0.01em;
          color: #f1f5f9;
          margin: 1.3em 0 0.5em 0;
          padding-left: 11px;
          border-left: 3px solid var(--edu-accent, #60a5fa);
          line-height: 1.3;
        }
        .edu-bold {
          font-weight: 600;
          color: #f1f5f9;
        }
        .edu-link {
          color: var(--edu-accent, #60a5fa);
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
          cursor: pointer;
        }
        .edu-content img {
          border: 1px solid #35486b;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          max-width: 100%;
          height: auto;
          display: block;
          margin: 14px auto;
        }
        .edu-content strong { color: #f1f5f9; }

        /* Completion seal */
        .edu-seal-arc {
          animation: eduSealArc 0.55s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        @keyframes eduSealArc {
          from { stroke-dasharray: 0 ${SEAL_C}; }
          to { stroke-dasharray: ${SEAL_DASH} ${SEAL_C - SEAL_DASH}; }
        }
        .edu-seal-tick {
          stroke-dasharray: ${TICK_LEN};
          animation: eduSealTick 0.45s cubic-bezier(0.65, 0, 0.35, 1) 0.85s both;
        }
        @keyframes eduSealTick {
          from { stroke-dashoffset: ${TICK_LEN}; }
          to { stroke-dashoffset: 0; }
        }
        .edu-seal-glow {
          position: absolute;
          inset: -16px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(52,211,153,0.20), transparent 68%);
          opacity: 0;
          animation: eduSealGlow 0.9s ease 1.1s both;
        }
        @keyframes eduSealGlow {
          from { opacity: 0; transform: scale(0.7); }
          to { opacity: 1; transform: scale(1); }
        }

        /* Completion overlay */
        .edu-celebrate-backdrop { animation: eduFadeIn 0.35s ease both; }
        .edu-celebrate-card { animation: eduCardIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .edu-celebrate-line { animation: eduCelebrateLine 0.5s ease both; }
        @keyframes eduFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes eduCardIn {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to { opacity: 1; transform: none; }
        }
        @keyframes eduCelebrateLine {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Module completion toast */
        .edu-module-toast { animation: eduToastIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }
        .edu-module-toast-out { animation: eduToastOut 0.3s ease both; }
        @keyframes eduToastIn {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: none; }
        }
        @keyframes eduToastOut {
          to { opacity: 0; transform: translateY(8px); }
        }
        .edu-toast-ring {
          stroke-dasharray: 94.25;
          stroke-dashoffset: 0;
          animation: eduToastRing 0.7s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both;
        }
        @keyframes eduToastRing {
          from { stroke-dashoffset: 94.25; }
          to { stroke-dashoffset: 0; }
        }

        /* Lesson tick + progress bar */
        .edu-tick-pop { animation: eduTickPop 0.32s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        @keyframes eduTickPop {
          0% { opacity: 0; transform: scale(0.4); }
          60% { opacity: 1; transform: scale(1.15); }
          100% { opacity: 1; transform: scale(1); }
        }
        .edu-bar-sweep {
          position: absolute;
          inset: 0;
          opacity: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent);
          animation: eduBarSweep 0.9s ease 0.1s;
        }
        @keyframes eduBarSweep {
          from { opacity: 1; transform: translateX(-100%); }
          to { opacity: 1; transform: translateX(220%); }
        }

        @media (prefers-reduced-motion: reduce) {
          .edu-seal-arc, .edu-seal-tick, .edu-seal-glow,
          .edu-celebrate-backdrop, .edu-celebrate-card, .edu-celebrate-line,
          .edu-module-toast, .edu-toast-ring,
          .edu-tick-pop, .edu-bar-sweep { animation: none; }
        }
      `}</style>

      {/* Header + course progress */}
      <div style={styles.header}>
        <h1 style={styles.title}>Education</h1>
        <p style={styles.subtitle}>Master profitable betting strategies</p>
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
            <span style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5b6678' }}>
              Your progress
            </span>
            <span style={{ fontSize: '11.5px', fontWeight: '700', color: doneCount > 0 ? '#34d399' : '#5b6678', fontVariantNumeric: 'tabular-nums' }}>
              {doneCount}/{TOTAL_LESSONS} · {pct}%
            </span>
          </div>
          <div style={{ position: 'relative', height: '6px', background: '#1a2336', borderRadius: '999px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#34d399', borderRadius: '999px', transition: 'width 0.4s ease' }} />
            {/* Remounts on every change so the sweep replays as progress advances. */}
            {doneCount > 0 && <div key={doneCount} className="edu-bar-sweep" />}
          </div>
        </div>
      </div>

      {/* Module Accordion */}
      {Object.entries(educationData)
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([moduleId, module]) => {
        const mId = parseInt(moduleId)
        const accent = moduleAccent(mId)
        const lessonIds = Object.keys(module.lessons).map(Number)
        const moduleDone = lessonIds.filter(l => isDone(mId, l)).length
        const moduleComplete = moduleDone === lessonIds.length
        const isExpanded = !!expandedModules[moduleId]
        return (
        <div key={moduleId} style={styles.moduleCard}>
          {/* Module Header */}
          <div style={styles.moduleHeader} onClick={() => handleModuleClick(mId)}>
            <span style={{
              width: '28px', height: '28px', minWidth: '28px',
              borderRadius: '8px',
              background: `${accent}1f`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px'
            }}>{module.icon}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {mId}. {module.title}
            </span>
            {moduleComplete ? (
              <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(52,211,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
            ) : (
              <span style={{ fontSize: '11.5px', color: '#5b6678', fontWeight: '600', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                {moduleDone}/{lessonIds.length}
              </span>
            )}
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5b6678" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>

          {/* Lessons List - Nested Accordion */}
          {isExpanded && (
            <div id={`module-lessons-${moduleId}`} style={{ padding: '0 12px 12px 12px' }}>
              {Object.entries(module.lessons)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([lessonId, lesson], idx, entries) => {
                  const lId = parseInt(lessonId)
                  const lessonKey = `${moduleId}-${lessonId}`
                  const expanded = !!expandedLessons[lessonKey]
                  const done = isDone(mId, lId)
                  const showGroup = lesson.group && (idx === 0 || entries[idx - 1][1].group !== lesson.group)
                  return (
                    <div key={lessonId} style={{ marginBottom: '8px' }}>
                      {showGroup && (
                        <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#5b6678', padding: '6px 2px 4px' }}>
                          {lesson.group}
                        </div>
                      )}
                      {/* Lesson Header - Clickable */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '12px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#f1f5f9',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          border: `1px solid ${expanded ? accent + '55' : '#1b2438'}`,
                          background: expanded ? `${accent}14` : '#101b30'
                        }}
                        onClick={() => handleLessonClick(moduleId, lessonId)}
                      >
                        {/* Completion circle — tap to toggle */}
                        <span
                          onClick={(e) => { e.stopPropagation(); setDone(mId, lId, !done) }}
                          title={done ? 'Mark as not complete' : 'Mark as complete'}
                          style={{
                            width: '17px', height: '17px', minWidth: '17px',
                            borderRadius: '50%',
                            border: `1.5px solid ${done ? '#34d399' : '#3a4760'}`,
                            background: done ? '#34d399' : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                        >
                          {done && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0a0f1a" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"
                              className={justTicked === `${mId}-${lId}` ? 'edu-tick-pop' : undefined}>
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>{lesson.shortTitle || lesson.title}</span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5b6678" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </div>

                      {/* Lesson Content - Shows when expanded */}
                      {expanded && (
                        <div id={`lesson-${moduleId}-${lessonId}`} style={{ ...styles.contentCard, margin: '8px 0 0 0' }}>
                          <div style={{ marginBottom: '18px', paddingBottom: '14px', borderBottom: '1px solid #1b2438' }}>
                            <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.09em', textTransform: 'uppercase', color: accent, marginBottom: '8px' }}>
                              Module {mId} — {module.title} · Lesson {idx + 1} of {entries.length}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{
                                width: '34px', height: '34px', minWidth: '34px',
                                borderRadius: '10px',
                                background: `${accent}1f`,
                                border: `1px solid ${accent}40`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '17px'
                              }}>{module.icon}</span>
                              <h2 style={{ fontSize: '20px', fontWeight: '800', letterSpacing: '-0.01em', color: '#f1f5f9', margin: 0, lineHeight: 1.2 }}>
                                {lesson.title}
                              </h2>
                            </div>
                          </div>
                          <div
                            className="edu-content"
                            style={{ color: '#cfd8e3', fontSize: '14.5px', lineHeight: '1.7', '--edu-accent': accent }}
                            onClick={(e) => {
                              const lessonLink = e.target.closest('[data-lesson]')
                              if (lessonLink) {
                                const [m, l] = lessonLink.dataset.lesson.split('-')
                                setExpandedModules({ [m]: true })
                                setExpandedLessons(prev => ({ ...prev, [`${m}-${l}`]: true }))
                                setTimeout(() => {
                                  const el = document.getElementById(`lesson-${m}-${l}`)
                                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                }, 150)
                                return
                              }
                              const link = e.target.closest('[data-page]')
                              if (link) window.dispatchEvent(new CustomEvent('bc-navigate', { detail: link.dataset.page }))
                            }}
                            dangerouslySetInnerHTML={{
                              __html: renderLessonContent(lesson.content)
                            }}
                          />

                          {/* Mark complete button */}
                          <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid #1b2438' }}>
                            {!done ? (
                              <button
                                onClick={() => setDone(mId, lId, true)}
                                style={{
                                  width: '100%', padding: '11px', borderRadius: '9px', border: 'none',
                                  background: '#16a34a', color: '#ffffff', fontSize: '14px', fontWeight: '700',
                                  cursor: 'pointer', fontFamily: 'inherit',
                                  boxShadow: '0 2px 10px rgba(22, 163, 74, 0.25)'
                                }}
                              >
                                Mark complete
                              </button>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontSize: '13.5px', fontWeight: '700', color: '#34d399' }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Completed
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
            </div>
          )}
        </div>
        )
      })}

      {/* Module completion toast — sits above the bottom tab bar */}
      {moduleToast && (
        <div
          className={moduleToast.leaving ? 'edu-module-toast edu-module-toast-out' : 'edu-module-toast'}
          style={{
            position: 'fixed', left: '12px', right: '12px',
            bottom: 'calc(66px + env(safe-area-inset-bottom, 0px))',
            zIndex: 998,
            display: 'flex', alignItems: 'center', gap: '12px',
            background: '#0d1526', border: '1px solid #1b2438', borderRadius: '12px',
            padding: '12px 16px 12px 13px',
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.5)'
          }}
        >
          <svg width="32" height="32" viewBox="0 0 36 36" style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
            <circle cx="18" cy="18" r="15" fill="none" stroke="#16203a" strokeWidth="3" />
            <circle cx="18" cy="18" r="15" fill="none" stroke={moduleAccent(moduleToast.mId)} strokeWidth="3" strokeLinecap="round" className="edu-toast-ring" />
          </svg>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: moduleAccent(moduleToast.mId), marginBottom: '3px' }}>
              Module {moduleToast.mId} complete
            </div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {educationData[moduleToast.mId].title}
            </div>
          </div>
        </div>
      )}

      {/* Course completion overlay */}
      {celebrating && (
        <div
          className="edu-celebrate-backdrop"
          onClick={() => setCelebrating(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1001,
            background: 'rgba(5, 9, 16, 0.86)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', overflowY: 'auto'
          }}
        >
          <div
            className="edu-celebrate-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0d1526', border: '1px solid #1b2438', borderRadius: '16px',
              padding: '30px 22px 26px', width: '100%', maxWidth: '400px', textAlign: 'center',
              boxShadow: '0 24px 70px rgba(0, 0, 0, 0.55)'
            }}
          >
            <CompletionSeal />
            <div className="edu-celebrate-line" style={{ animationDelay: '1.2s', fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#34d399', marginBottom: '9px' }}>
              Course complete
            </div>
            <h2 className="edu-celebrate-line" style={{ animationDelay: '1.3s', fontSize: '22px', fontWeight: '800', letterSpacing: '-0.02em', color: '#f1f5f9', margin: '0 0 10px', lineHeight: 1.25 }}>
              You've finished every lesson
            </h2>
            <p className="edu-celebrate-line" style={{ animationDelay: '1.4s', margin: '0 0 20px', fontSize: '13px', lineHeight: 1.6, color: '#8b97ab' }}>
              Now you have a complete understanding of every strategy. Put it to work and start making some money!
            </p>
            <div className="edu-celebrate-line" style={{ animationDelay: '1.5s', display: 'flex', borderTop: '1px solid #1b2438', borderBottom: '1px solid #1b2438', marginBottom: '20px' }}>
              {[['Modules', MODULE_COUNT, ''], ['Lessons', TOTAL_LESSONS, ''], ['Complete', 100, '%']].map(([label, value, suffix], i) => (
                <div key={label} style={{ flex: 1, padding: '13px 2px', borderLeft: i === 0 ? 'none' : '1px solid #1b2438' }}>
                  <div style={{ fontSize: '19px', fontWeight: '800', letterSpacing: '-0.02em', color: '#f1f5f9', fontVariantNumeric: 'tabular-nums' }}>
                    <CountUp end={value} delay={1650} />{suffix}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.07em', textTransform: 'uppercase', color: '#5b6678', marginTop: '3px' }}>
                    {label}
                  </div>
                </div>
              ))}
            </div>
            <div className="edu-celebrate-line" style={{ animationDelay: '1.6s', display: 'flex', flexDirection: 'column', gap: '9px' }}>
              <button
                onClick={() => { setCelebrating(false); window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'dashboard' })) }}
                style={{
                  width: '100%', padding: '12px', borderRadius: '9px', border: 'none',
                  background: '#2563eb', color: '#ffffff', fontSize: '14px', fontWeight: '700',
                  fontFamily: 'inherit', cursor: 'pointer'
                }}
              >
                Go to dashboard →
              </button>
              <button
                onClick={() => setCelebrating(false)}
                style={{
                  width: '100%', padding: '12px', borderRadius: '9px', border: '1px solid #26354f',
                  background: 'transparent', color: '#94a3b8', fontSize: '14px', fontWeight: '600',
                  fontFamily: 'inherit', cursor: 'pointer'
                }}
              >
                Back to lessons
              </button>
            </div>
            {/* Support footer */}
            <div className="edu-celebrate-line" style={{ animationDelay: '1.7s', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #1b2438' }}>
              <div style={{ fontSize: '12px', color: '#8b97ab', marginBottom: '10px' }}>
                Still need help with something?
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="https://discord.gg/BV4dVEnx73" target="_blank" rel="noopener noreferrer" style={styles.supportLink}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                  </svg>
                  Ask the team on Discord
                </a>
                <a href="mailto:support@bookiecharity.com" style={styles.supportLink}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  support@bookiecharity.com
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
